import React from 'react';

import { Trans } from '@lingui/react/macro';
import { Button } from '@repo/videa-components';

import type { TableColumn } from '@/common-components/composed/sortableTable/SortableTable';
import { SortableTable } from '@/common-components/composed/sortableTable/SortableTable';
import { StatusChip } from '@/common-components/elements/StatusChip/StatusChip';

import { useLocalizedFormatDate } from '../../claims/freeClaims/state';
import type {
  PaginationInfo,
  ReferralRecord,
  ReferralStatus,
} from '../referralPageTypes';

import { PdfButton } from './PdfButton';
import { ReferralPatientNameCell } from './ReferralPatientNameCell';

// Map referral statuses to StatusChip variants and labels
const getReferralStatusConfig = (status: ReferralStatus) => {
  switch (status) {
    case 'submitted':
    case 'completed':
      return {
        variant: 'success' as const,
        label: status === 'submitted' ? 'Submitted' : 'Completed',
      };
    case 'needs_referral':
      return {
        variant: 'warning' as const,
        label: 'Needs Referral',
      };
    case 'in_progress':
      return {
        variant: 'warning' as const,
        label: 'In Progress',
      };
    case 'failed':
      return {
        variant: 'error' as const,
        label: 'Needs Resubmission',
      };
    case 'not_started':
    case 'cancelled':
      return {
        variant: 'default' as const,
        label: status === 'not_started' ? 'Not Started' : 'Cancelled',
      };
    default:
      return {
        variant: 'default' as const,
        label: 'Unknown',
      };
  }
};

interface ReferralsTableProps {
  referrals: ReferralRecord[];
  pagination?: PaginationInfo;
  sortField?: string;
  sortDirection?: 'asc' | 'desc';
  isSorting?: boolean;
  onNavigateToUnifiedAppointment: (
    imagingPatientVideaId: string,
    unifiedAppointmentId: string,
  ) => void;
  onPageChange?: (page: number) => void;
  onSort?: (field: string, direction: 'asc' | 'desc') => void;
  onResubmitReferral?: (referralId: string) => void;
  onSubmitReferral?: (referral: ReferralRecord) => void;
  onUnflagReferral?: (referralId: string) => void;
}

export const ReferralsTable = React.memo(
  ({
    referrals,
    pagination,
    sortField,
    sortDirection,
    isSorting,
    onNavigateToUnifiedAppointment,
    onPageChange,
    onSort,
    onResubmitReferral,
    onSubmitReferral,
    onUnflagReferral,
  }: ReferralsTableProps) => {
    const { page, limit, total, totalPages } = pagination || {
      page: 1,
      limit: 20,
      total: 0,
      totalPages: 1,
    };

    // Calculate pagination display values
    const startItem = (page - 1) * limit + 1;
    const endItem = Math.min(page * limit, total);

    // Use localized date formatting
    const formatDate = useLocalizedFormatDate();

    // Define columns for SortableTable
    const columns: TableColumn<ReferralRecord>[] = [
      {
        key: 'patient_name',
        header: <Trans>Patient</Trans>,
        width: 'vai-w-[200px]',
        isSortable: true,
        render: (referral, index) => (
          <ReferralPatientNameCell
            referral={referral}
            index={index}
            onNavigateToUnifiedAppointment={onNavigateToUnifiedAppointment}
          />
        ),
      },
      {
        key: 'sent_by',
        header: <Trans>Sent By</Trans>,
        width: 'vai-w-[180px]',
        render: (referral) => (
          <div className="vai-text-sm vai-text-gray-900">
            {referral.user ? (
              `${referral.user.firstName} ${referral.user.lastName}`
            ) : (
              <span className="vai-text-gray-400 vai-italic">
                <Trans>Unknown</Trans>
              </span>
            )}
          </div>
        ),
      },
      {
        key: 'updated_at',
        header: <Trans>Last Updated</Trans>,
        width: 'vai-w-[160px]',
        isSortable: true,
        render: (referral) => (
          <div className="vai-text-sm vai-text-gray-900">
            {formatDate(referral.updatedAt)}
          </div>
        ),
      },
      {
        key: 'specialty',
        header: <Trans>Specialty</Trans>,
        width: 'vai-w-[180px]',
        render: (referral) => (
          <div className="vai-text-sm vai-text-gray-900">
            {referral.specialtyName}
          </div>
        ),
      },
      {
        key: 'destinationPractice',
        header: <Trans>Specialty Practice</Trans>,
        width: 'vai-w-[250px]',
        render: (referral) => (
          <div className="vai-text-sm vai-text-gray-900">
            {referral.destinationPracticeName}
          </div>
        ),
      },
      {
        key: 'status',
        header: <Trans>Status</Trans>,
        width: 'vai-min-w-[200px]',
        render: (referral) => {
          const statusConfig = getReferralStatusConfig(referral.status);

          return (
            <StatusChip
              text={statusConfig.label}
              variant={statusConfig.variant}
            />
          );
        },
      },
      {
        key: 'actions',
        header: <Trans>Actions</Trans>,
        width: 'vai-w-[160px]',
        render: (referral) => (
          <div className="vai-flex vai-gap-2 vai-justify-end">
            {referral.imagingPatientVideaId && (
              <Button
                variant="outline"
                size="sm"
                className="vai-flex vai-items-center vai-space-x-1.5 vai-px-3 vai-py-1.5 vai-text-blue-600 vai-border-blue-200 hover:vai-bg-blue-50 vai-h-7"
                onClick={(e) => {
                  e.stopPropagation();
                  onNavigateToUnifiedAppointment(
                    referral.imagingPatientVideaId!,
                    referral.unifiedAppointmentId,
                  );
                }}
              >
                <Trans>See Visit</Trans>
              </Button>
            )}
            {referral.status === 'submitted' && (
              <PdfButton referral={referral} />
            )}
            {referral.status === 'failed' && onResubmitReferral && (
              <Button
                size="sm"
                className="vai-flex vai-items-center vai-space-x-1.5 vai-px-3 vai-py-1.5 vai-bg-blue-600 vai-text-white hover:vai-bg-blue-700 vai-h-7"
                onClick={(e) => {
                  e.stopPropagation();
                  onResubmitReferral(referral.id);
                }}
              >
                <Trans>Resubmit</Trans>
              </Button>
            )}
            {referral.status === 'needs_referral' && onSubmitReferral && (
              <Button
                size="sm"
                className="vai-flex vai-items-center vai-space-x-1.5 vai-px-3 vai-py-1.5 vai-bg-blue-600 vai-text-white hover:vai-bg-blue-700 vai-h-7"
                onClick={(e) => {
                  e.stopPropagation();
                  onSubmitReferral(referral);
                }}
              >
                <Trans>Submit</Trans>
              </Button>
            )}
            {referral.status === 'needs_referral' && onUnflagReferral && (
              <Button
                variant="outline"
                size="sm"
                className="vai-flex vai-items-center vai-space-x-1.5 vai-px-3 vai-py-1.5 vai-text-gray-600 vai-border-gray-300 hover:vai-bg-gray-50 vai-h-7"
                onClick={(e) => {
                  e.stopPropagation();
                  onUnflagReferral(referral.id);
                }}
              >
                <Trans>Unflag</Trans>
              </Button>
            )}
          </div>
        ),
      },
    ];

    return (
      <div className="vai-w-full vai-flex vai-flex-1 vai-flex-col vai-min-h-0 vai-overflow-hidden">
        <SortableTable
          data={referrals}
          columns={columns}
          rowKey={(referral) => `referral-${referral.id}`}
          sortField={sortField}
          sortDirection={sortDirection}
          onSort={onSort}
          isSorting={isSorting}
          className="vai-min-h-0 vai-flex-1"
        />

        {pagination && totalPages > 1 && (
          <div className="vai-flex vai-items-center vai-justify-between vai-p-4 vai-border-t vai-border-gray-200">
            <div className="vai-text-sm vai-text-gray-500">
              <Trans>
                Showing {startItem} to {endItem} of {total} results
              </Trans>
            </div>

            <div className="vai-flex vai-items-center vai-space-x-2">
              <Button
                variant="outline"
                size="sm"
                disabled={!pagination?.hasPrev}
                onClick={() => onPageChange?.(page - 1)}
              >
                <Trans>Previous</Trans>
              </Button>

              <div className="vai-flex vai-items-center vai-space-x-1">
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  const pageNum = i + Math.max(1, page - 2);
                  if (pageNum > totalPages) return null;

                  return (
                    <Button
                      key={pageNum}
                      variant={pageNum === page ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => onPageChange?.(pageNum)}
                      className="vai-w-8 vai-h-8 vai-p-0"
                    >
                      {pageNum}
                    </Button>
                  );
                })}
              </div>

              <Button
                variant="outline"
                size="sm"
                disabled={!pagination?.hasNext}
                onClick={() => onPageChange?.(page + 1)}
              >
                <Trans>Next</Trans>
              </Button>
            </div>
          </div>
        )}
      </div>
    );
  },
);
